import { useQuery } from "@tanstack/react-query";
import { getJson, postJson } from "./client";

export interface SupersetDashboardEmbed {
  /** Legacy standalone-iframe URL. Only renders for a browser that already holds a
   *  Superset session, so it shows a login page for ordinary Athena users. */
  url: string;
  baseUrl: string;
  dashboard: string;
  /** Embedded-registration id the Superset SDK mounts. Null if the dashboard has not
   *  been registered for embedding, in which case the embed cannot render. */
  uuid: string | null;
  /** Origin the SDK loads Superset from. */
  supersetDomain: string;
}

export interface SupersetGuestToken {
  token: string;
  uuid: string;
  supersetDomain: string;
  /** The RLS clauses the backend actually put in the token. */
  rls?: string[];
  /** Filter values the backend refused to translate. */
  warnings?: string[];
}

/**
 * What the page's filter bar narrows the embedded dashboard to.
 *
 * Sent as plain values, NOT as SQL: prc-api maps them through an allowlist into the guest
 * token's RLS clauses. That is the only mechanism that actually filters an embedded
 * dashboard here — Superset does not bind arbitrary query params to native filters, and the
 * deployment has no template processing, so the previous `urlParams={{scope}}` approach only
 * ever changed the panel heading.
 */
/**
 * What the page narrows an embedded dashboard to. Every field becomes a guest-token RLS
 * clause in prc-api (`_rls_clauses`) — never a URL param, never a client-side filter — so
 * the viewer cannot widen it and there is no filter bar inside the frame.
 *
 * Adding a field here is only half the job: the clause has to be built server-side against
 * a column the dashboard's dataset actually has. `investor` was sent from the page for a
 * while before either existed, and was accepted and silently dropped the whole time.
 */
export interface SupersetFilters {
  scope?: string;
  facility?: string;
  /** "LOAN" | "LEASE" | "ALL". Independent of `facility`; the backend intersects them. */
  product?: string;
  investor?: string;
  /** YYYY-MM-DD. Resolves server-side to the newest snapshot at or before this day. */
  asOf?: string;
}

/** Embed details for one PRC Superset dashboard. The backend looks the uuid up from
 *  Superset rather than storing it, so it cannot go stale.
 *
 *  `key` selects which dashboard from the backend's registry — "prc" is the original
 *  formatted view, "monitor" is the one over v_prc_monitor that Snapshot filters by
 *  scope. Omitting it means "prc", which is what the only caller did before there was
 *  more than one dashboard. */
export function useSupersetDashboard(key?: string) {
  return useQuery({
    queryKey: ["prc-superset-dashboard", key ?? "prc"],
    queryFn: () =>
      getJson<SupersetDashboardEmbed>(
        `portfolio-risk-concentrations/superset/dashboard${
          key ? `?key=${encodeURIComponent(key)}` : ""
        }`,
      ),
  });
}

/** Mints a guest token. Not a hook: the Superset SDK calls this itself, both on mount and
 *  again whenever the token nears expiry (they are deliberately short-lived), so it has to
 *  be an ordinary awaitable rather than something tied to React's render cycle.
 *
 *  Takes the dashboard key rather than reading it from context for the same reason — the
 *  SDK holds onto this function and calls it long after the component that made it has
 *  re-rendered, so the key has to be bound into the closure. */
export async function fetchSupersetGuestToken(
  key?: string,
  filters?: SupersetFilters,
): Promise<string> {
  const res = await postJson<SupersetGuestToken>(
    "portfolio-risk-concentrations/superset/guest-token",
    {
      ...(key ? { key } : {}),
      // Omitted when there is nothing to narrow by, so a caller that does not filter gets a
      // token with no RLS rather than one carrying an empty clause list.
      //
      // Tests EVERY field, not `scope || facility`. Snapshot stopped sending `scope` (it is
      // inert on the summary dashboard) and that gate would then have hung the whole filter
      // set off `facility` alone — a page filtering by investor or as-of date only would
      // have sent nothing and shown an unfiltered dashboard.
      ...(filters && Object.values(filters).some(Boolean) ? { filters } : {}),
    },
  );
  if (res.warnings?.length) {
    // Not thrown: the dashboard renders correctly, just unfiltered. Silence here is what
    // made the old broken filter so hard to notice.
    console.warn("[prc] Superset filter not applied:", res.warnings.join(" "));
  }
  return res.token;
}
